import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { SpeedingEvent } from '../types';
import { getHealth } from '../api/health';
import { handleSessionReplaced } from '../api/session';
import { refreshAccessToken } from '../api/tokenRefresh';
import { isTokenExpiringSoon } from '../utils/jwt';

const API_URL = window.__ENV__?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:20002';

// ISS-043 / ADR-004 — exponential backoff + 1시간 자동 상한 + 이벤트 부활
const BACKOFF_STEPS_MS = [5000, 10000, 20000, 40000, 60000];
const BACKOFF_JITTER_RATIO = 0.2;
const MAX_AUTO_RECONNECT_DURATION_MS = 60 * 60 * 1000;

export type AlertType = 'speeding' | 'company_pending' | 'system';

export interface SSEAlert {
  id: string;
  type: AlertType;
  title: string;
  data: Record<string, unknown>;
  timestamp: string;
  dismissed: boolean;
}

interface SSEContextType {
  isConnected: boolean;
  autoStopped: boolean;
  reconnect: () => Promise<void>;
  lastEvent: SpeedingEvent | null;
  events: SpeedingEvent[];
  alerts: SSEAlert[];
  unreadCount: number;
  dismissAlert: (id: string) => void;
  deleteAlert: (id: string) => void;
}

export const SSEContext = createContext<SSEContextType>({
  isConnected: false,
  autoStopped: false,
  reconnect: async () => {},
  lastEvent: null,
  events: [],
  alerts: [],
  unreadCount: 0,
  dismissAlert: () => {},
  deleteAlert: () => {},
});

let alertIdCounter = 0;

export const SSEProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, accessToken, role } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [autoStopped, setAutoStopped] = useState(false);
  const [lastEvent, setLastEvent] = useState<SpeedingEvent | null>(null);
  const [events, setEvents] = useState<SpeedingEvent[]>([]);
  const [alerts, setAlerts] = useState<SSEAlert[]>([]);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptIndexRef = useRef<number>(0);
  const reconnectStartedAtRef = useRef<number | null>(null);

  const isStaff = role === 'super_admin' || role === 'admin' || role === 'manager';

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, dismissed: true } : a)));
  }, []);

  const deleteAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const computeBackoffMs = useCallback((idx: number): number => {
    const step = BACKOFF_STEPS_MS[Math.min(idx, BACKOFF_STEPS_MS.length - 1)];
    const jitter = step * BACKOFF_JITTER_RATIO * (Math.random() * 2 - 1);
    return Math.max(0, step + jitter);
  }, []);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!isStaff) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    // ISS-069 — 항상 최신 토큰 사용. silent refresh 후에는 localStorage 가
    // AuthContext state(accessToken) 보다 신선할 수 있다.
    let token = localStorage.getItem('access_token') || accessToken;
    if (!token) return;

    // ISS-069 — 만료/임박(<60s) 토큰이면 EventSource 생성 전에 선제 refresh.
    // EventSource 는 401 을 상태코드로 노출하지 않아 스스로 만료를 감지하지 못한다.
    // 만료 토큰으로 연결하면 onerror → 같은 죽은 토큰 재시도의 무한 401 루프에 빠진다.
    if (isTokenExpiringSoon(token, 60)) {
      try {
        token = await refreshAccessToken();
      } catch {
        // refresh 불가 = 세션 종료. 다음 API 호출의 axios 인터셉터가 /login 으로 리다이렉트한다.
        setIsConnected(false);
        return;
      }
    }

    closeEventSource();
    const es = new EventSource(`${API_URL}/sse?token=${token}`);

    es.onopen = () => {
      setIsConnected(true);
      setAutoStopped(false);
      attemptIndexRef.current = 0;
      reconnectStartedAtRef.current = null;
    };

    es.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data);
        const eventType: string = data.type || 'speeding';

        // ISS-062 — 단일 세션 강제. 새 로그인 발생 시 backend 가 SSE 로 즉시 알림 →
        // 다음 API 호출 대기 없이 forced logout 흐름 진입. axios 인터셉터의 SESSION_REPLACED
        // 분기와 동일 동작 (토큰 폐기 + 모달 플래그 + /login redirect).
        if (eventType === 'session_replaced') {
          handleSessionReplaced();
          return;
        }

        // AUDIT-001 (S-44) — role 변경 시 backend 가 SSE 로 typed 이벤트 전달.
        // session_replaced 와 동일한 forced logout 흐름. 이전에는 raw dict 가 전달되어
        // speeding alert 로 오인되었음 (phantom alert). 이제 명시적 타입으로 처리.
        if (eventType === 'role_changed') {
          handleSessionReplaced();
          return;
        }

        const normalizedType = eventType === 'caution' ? 'speeding' : eventType;

        if (normalizedType === 'speeding') {
          const event = data as SpeedingEvent;
          setLastEvent(event);
          setEvents((prev) => {
            const idx = prev.findIndex(e => e.id === event.id);
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = event;
              return updated;
            }
            return [event, ...prev].slice(0, 50);
          });
        }

        const alertTypes: AlertType[] = ['speeding', 'company_pending', 'system'];
        if (alertTypes.includes(normalizedType as AlertType)) {
          alertIdCounter += 1;
          const alert: SSEAlert = {
            id: `sse-${alertIdCounter}`,
            type: normalizedType as AlertType,
            title: data.title || data.message || eventType,
            data,
            timestamp: new Date().toISOString(),
            dismissed: false,
          };
          setAlerts((prev) => [alert, ...prev].slice(0, 100));
        }
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => {
      setIsConnected(false);
      es.close();
      eventSourceRef.current = null;

      if (reconnectStartedAtRef.current === null) {
        reconnectStartedAtRef.current = Date.now();
      }
      const elapsed = Date.now() - reconnectStartedAtRef.current;
      if (elapsed >= MAX_AUTO_RECONNECT_DURATION_MS) {
        setAutoStopped(true);
        return;
      }

      const delay = computeBackoffMs(attemptIndexRef.current);
      attemptIndexRef.current += 1;

      clearReconnectTimeout();
      reconnectTimeoutRef.current = setTimeout(() => {
        if (isAuthenticated && isStaff) connect();
      }, delay);
    };

    eventSourceRef.current = es;
  }, [accessToken, isAuthenticated, isStaff, closeEventSource, clearReconnectTimeout, computeBackoffMs]);

  const resetBackoffAndReconnect = useCallback(() => {
    clearReconnectTimeout();
    attemptIndexRef.current = 0;
    reconnectStartedAtRef.current = null;
    setAutoStopped(false);
    connect();
  }, [clearReconnectTimeout, connect]);

  const reconnect = useCallback(async () => {
    await getHealth();
    resetBackoffAndReconnect();
  }, [resetBackoffAndReconnect]);

  useEffect(() => {
    if (!isAuthenticated || !isStaff || !accessToken) {
      closeEventSource();
      clearReconnectTimeout();
      setIsConnected(false);
      setAutoStopped(false);
      attemptIndexRef.current = 0;
      reconnectStartedAtRef.current = null;
      return;
    }

    attemptIndexRef.current = 0;
    reconnectStartedAtRef.current = null;
    setAutoStopped(false);
    connect();

    return () => {
      closeEventSource();
      clearReconnectTimeout();
    };
  }, [isAuthenticated, accessToken, isStaff, connect, closeEventSource, clearReconnectTimeout]);

  useEffect(() => {
    if (!isAuthenticated || !isStaff) return;

    const handleOnline = () => resetBackoffAndReconnect();
    const handleFocus = () => resetBackoffAndReconnect();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        resetBackoffAndReconnect();
      } else {
        clearReconnectTimeout();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, isStaff, resetBackoffAndReconnect, clearReconnectTimeout]);

  const unreadCount = alerts.filter((a) => !a.dismissed).length;

  return (
    <SSEContext.Provider
      value={{ isConnected, autoStopped, reconnect, lastEvent, events, alerts, unreadCount, dismissAlert, deleteAlert }}
    >
      {children}
    </SSEContext.Provider>
  );
};
